import { FC, useState, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from 'app/components/TextField';
import { Checkbox, Text } from '@concepta/react-material-ui';
import {
  FormContainer,
  VacationHome,
  LocationMoment,
  LocationType,
} from 'app/hooks/useGlobalForm';
import useDebounce from 'app/hooks/useDebounce';
import isEqual from 'lodash/isEqual';
import ErrorText from 'app/components/ErrorText';
import RestrictedLocationModal from 'app/components/RestrictedLocationModal';
import ResortIdentifiedModal from 'app/components/ResortIdentifiedModal';
import {
  checkRestrictedAddress,
  checkResortAddress,
  normalizeInput,
} from 'utils/validators/addressMatcher';
import useLocalStorageState from 'use-local-storage-state';
import { ResortInterface, SystemParameterInterface } from 'ks-common';
import { useNavigate } from '@concepta/react-router';
import { GlobalStateContainer } from 'app/hooks/useGlobalState';

interface Props {
  id: LocationMoment;
  error?: string;
  fieldsErrors?: Record<string, string>;
}

interface Row {
  name: keyof VacationHome;
  title: string;
  maxLength?: number;
}

const LocationVacationHome: FC<Props> = ({ id, error, fieldsErrors }) => {
  const { currentProduct, updateCurrentProduct } = FormContainer.useContainer();
  const { globalState } = GlobalStateContainer.useContainer();
  const [vacationHomeForm, setVacationHomeForm] = useState<VacationHome>(
    currentProduct[id]?.vacationHome || {},
  );
  const debouncedVacationHomeForm = useDebounce<VacationHome>(
    vacationHomeForm,
    300,
  );
  const [systemParams] =
    useLocalStorageState<SystemParameterInterface[]>('systemParams');
  const [restrictionModalOpen, setRestrictionModalOpen] = useState(false);
  const [restrictedLocationInfo, setRestrictedLocationInfo] = useState<{
    name: string;
  } | null>(null);
  const [resortModalOpen, setResortModalOpen] = useState(false);
  const [matchedResorts, setMatchedResorts] = useState<ResortInterface[]>([]);
  const lastCheckedAddressRef = useRef<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    updateCurrentProduct({
      [id]: {
        vacationHome: vacationHomeForm,
      },
    });
  }, [debouncedVacationHomeForm]);

  const sameAsCheckin = currentProduct.returnLocation?.sameAsCheckin;

  const vacationHome = currentProduct[id]?.vacationHome;
  const streetAddress = vacationHome?.streetAddress;
  const city = vacationHome?.city;
  const zip = vacationHome?.zip;
  const state = vacationHome?.state;
  const gateCode = vacationHome?.gateCode;
  const addressUnknown = vacationHome?.addressUnknown;

  const bannedList = useMemo(
    () =>
      systemParams
        ?.find(param => param.ParamName === 'Banned_List')
        ?.ParamValue?.split(';'),
    [systemParams],
  );

  const normalizedBannedList = useMemo(
    () =>
      bannedList
        ?.map(addr => normalizeInput(addr))
        .filter(addr => addr.length > 0),
    [bannedList],
  );

  useEffect(() => {
    if (
      id === LocationMoment.ReturnLocation &&
      sameAsCheckin &&
      vacationHome &&
      !isEqual(vacationHome, vacationHomeForm)
    ) {
      setVacationHomeForm(vacationHome);
    }
  }, [streetAddress, city, zip, state, gateCode, addressUnknown]);

  // Validate address against banned list on unmount
  useEffect(() => {
    return () => {
      if (!normalizedBannedList || !vacationHomeForm?.streetAddress) return;

      const refIndex = checkRestrictedAddress(
        vacationHomeForm.streetAddress,
        normalizedBannedList,
      );

      if (refIndex !== null) {
        updateCurrentProduct({
          [id]: {
            vacationHome: {
              ...vacationHomeForm,
              streetAddress: '',
            },
          },
        });
      }
    };
  }, []);

  const onChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const target = event?.target;
    if (target?.name) {
      setVacationHomeForm({ ...vacationHomeForm, [target.name]: target.value });

      // Reset last checked address when user modifies address fields
      if (['streetAddress', 'city', 'zip'].includes(target.name)) {
        lastCheckedAddressRef.current = null;
      }
    }
  };

  const handleCheckbox = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    updateCurrentProduct({
      [id]: {
        vacationHome: {
          addressUnknown: checked,
        },
      },
    });
  };

  const handleAddressFieldBlur = () => {
    if (resortModalOpen) return;

    const streetAddress = vacationHomeForm?.streetAddress || '';
    const city = vacationHomeForm?.city || '';
    const zip = vacationHomeForm?.zip || '';

    // At least one field must be filled
    if (!streetAddress && !city && !zip) return;

    // Build current address key to check for re-triggering
    const currentAddressKey = normalizeInput(`${streetAddress} ${city} ${zip}`);

    // Check banned list first (takes priority) - only if street address provided
    if (streetAddress && normalizedBannedList) {
      const refIndex = checkRestrictedAddress(
        streetAddress,
        normalizedBannedList,
      );

      if (refIndex !== null) {
        setRestrictedLocationInfo({
          name: bannedList?.[refIndex] ?? '',
        });
        setRestrictionModalOpen(true);
        return;
      }
    }

    if (lastCheckedAddressRef.current === currentAddressKey) {
      return;
    }

    const resortsList = globalState?.resortsList;
    const normalizedResortAddresses = globalState?.normalizedResortAddresses;
    if (resortsList?.length && normalizedResortAddresses?.length) {
      const matches = checkResortAddress(
        streetAddress,
        city,
        zip,
        resortsList,
        normalizedResortAddresses,
      );

      if (matches.length > 0) {
        setMatchedResorts(matches);
        setResortModalOpen(true);
      }
    }
  };

  const handleModalClose = () => {
    setRestrictionModalOpen(false);
    setVacationHomeForm({ ...vacationHomeForm, streetAddress: '' });
    setRestrictedLocationInfo(null);
  };

  const handleContactSupport = () => {
    setRestrictionModalOpen(false);
    navigate('/contact-us');
  };

  const handleResortConfirm = (resort: ResortInterface) => {
    setResortModalOpen(false);
    setMatchedResorts([]);

    // Check sameAsCheckin BEFORE any updates
    const shouldUpdateReturn =
      id === LocationMoment.DeliveryLocation &&
      currentProduct.returnLocation?.sameAsCheckin;

    updateCurrentProduct({
      [id]: {
        location: LocationType.Resort,
        resort: resort,
        vacationHome: undefined,
      },
    });

    // If sameAsCheckin was true, also update return location
    if (shouldUpdateReturn) {
      updateCurrentProduct({
        returnLocation: {
          sameAsCheckin: true,
          location: LocationType.Resort,
          resort: resort,
          vacationHome: undefined,
          time: currentProduct.deliveryLocation?.time ?? undefined,
        },
      });
    }
  };

  const handleResortModalClose = () => {
    const currentAddressKey = normalizeInput(
      `${vacationHomeForm.streetAddress || ''} ${vacationHomeForm.city || ''} ${
        vacationHomeForm.zip || ''
      }`,
    );
    lastCheckedAddressRef.current = currentAddressKey;

    setResortModalOpen(false);
    setMatchedResorts([]);
  };

  const handleResortContactSupport = () => {
    setResortModalOpen(false);
    setMatchedResorts([]);
    navigate('/contact-us');
  };

  const options: Row[] = [
    { name: 'streetAddress', title: 'Street Address', maxLength: 100 },
    { name: 'city', title: 'City', maxLength: 100 },
    { name: 'zip', title: 'ZIP', maxLength: 5 },
    { name: 'state', title: 'State', maxLength: 50 },
    {
      name: 'gateCode',
      title: 'Gate Code (if applicable)',
      maxLength: 100,
    },
  ];

  const checkboxLabel = `${
    id === LocationMoment.ReturnLocation ? 'Pickup' : 'Delivery'
  } address unknown at this time`;

  return (
    <Box>
      {options.map(({ name, title, maxLength }) => (
        <TextField
          key={name}
          name={name}
          variant="standard"
          fullWidth
          sx={{ m: 0 }}
          error={!!fieldsErrors?.[name]}
          helperText={fieldsErrors?.[name]}
          label={
            <Text fontWeight={400} fontSize="14px" letterSpacing="-0.5px">
              {title}
            </Text>
          }
          inputProps={{ ...(maxLength && { maxLength }) }}
          InputLabelProps={{ shrink: !!vacationHomeForm?.[name] }}
          onChange={onChange}
          value={vacationHomeForm?.[name]}
          {...(['streetAddress', 'city', 'zip'].includes(name) && {
            onBlur: handleAddressFieldBlur,
          })}
        />
      ))}

      <Box mt={2}>
        <Checkbox
          label={checkboxLabel}
          textProps={{ fontSize: '14px', fontWeight: 400 }}
          onChange={handleCheckbox}
          checked={currentProduct[id]?.vacationHome?.addressUnknown}
        />
      </Box>

      {error && <ErrorText>{error}</ErrorText>}

      <RestrictedLocationModal
        open={restrictionModalOpen}
        onClose={handleModalClose}
        onContactSupport={handleContactSupport}
        locationName={restrictedLocationInfo?.name || ''}
      />

      <ResortIdentifiedModal
        open={resortModalOpen}
        onClose={handleResortModalClose}
        onConfirm={handleResortConfirm}
        onContactSupport={handleResortContactSupport}
        matchedResorts={matchedResorts}
        userEnteredAddress={{
          streetAddress: vacationHomeForm.streetAddress || '',
          city: vacationHomeForm.city || '',
          zip: vacationHomeForm.zip || '',
        }}
      />
    </Box>
  );
};

export default LocationVacationHome;
